import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import InputNumber from '@/design_system/InputNumber';
import Message from '@/design_system/Message';
import { RadioItem, RadioItemGroup } from '@/design_system/Radio/RadioItem/RadioItem';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import { Brand, useUpdateBrand } from '@/models/brand';
import { EditButton, SaveButton } from '@/routes/SettingsBeta/components/SaveEditCancelButtons';

export const DepositPercentages = ({ brand }: { brand: Brand }) => {
  const { t } = useLingui();
  const { mutateAsync: updateBrand, isPending: isUpdateBrandPending } = useUpdateBrand(brand.id);
  const [partialDepositPercentage, setPartialDepositPercentage] = useState(
    brand.depositPercentages.find((depositPercentage) => ![0, 100].includes(depositPercentage))
  );
  const [showDepositPercentagesInputs, setShowDepositPercentagesInputs] = useState(
    !!brand.depositPercentages.length
  );
  const [isEditing, setIsEditing] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    const missingPartialDepositPercentage =
      partialDepositPercentage !== 0 && !partialDepositPercentage;
    const wrongPartialDepositPercentage =
      partialDepositPercentage === 0 ||
      (!!partialDepositPercentage &&
        (partialDepositPercentage < 1 || partialDepositPercentage > 99));
    const isNotInteger = !Number.isInteger(partialDepositPercentage);

    const hasError =
      missingPartialDepositPercentage || wrongPartialDepositPercentage || isNotInteger;

    if (hasError) {
      const error = missingPartialDepositPercentage
        ? t({
            id: 'settings.organization.data-catalogs.brands.tab.overview.deposit-percentages.errors.missing-partial-deposit-percentage',
            message: 'Partial deposit percentage must be defined',
          })
        : wrongPartialDepositPercentage
          ? t({
              id: 'settings.organization.data-catalogs.brands.tab.overview.deposit-percentages.errors.not-between-1-and-99',
              message: 'Partial deposit percentage must be between 1 and 99',
            })
          : isNotInteger
            ? t({
                id: 'settings.organization.data-catalogs.brands.tab.overview.deposit-percentages.errors.not-integer',
                message: 'Partial deposit percentage must be an integer',
              })
            : null;

      setShowErrors(true);
      setError(error);

      return;
    }

    setShowErrors(false);
    setError(null);
    await updateBrand({
      depositPercentages: [0, partialDepositPercentage, 100],
    });
    setIsEditing(false);
  };

  return (
    <Stack gap="0.5em">
      <Title as="h2" size="400" weight="medium">
        <Trans id="settings.organization.data-catalogs.brands.tab.overview.deposit-percentages">
          Deposit percentages
        </Trans>
      </Title>

      <RadioItemGroup
        name="enable-deposit-percentages"
        value={showDepositPercentagesInputs ? 'enabled' : 'disabled'}
        ariaLabel={t({
          id: 'settings.organization.data-catalogs.brands.tab.overview.deposit-percentages.enable',
          message: 'Enable deposit percentages',
        })}
        onChange={(newValue) => {
          if (newValue === 'enabled') {
            setShowDepositPercentagesInputs(true);
          } else {
            setShowDepositPercentagesInputs(false);
            setPartialDepositPercentage(undefined);
            updateBrand({ depositPercentages: [] });
          }
        }}
        disabled={isUpdateBrandPending}
      >
        <RadioItem
          value="enabled"
          ariaLabel={t({
            id: 'settings.organization.data-catalogs.brands.tab.overview.deposit-percentages.enabled',
            message: 'Enabled',
          })}
        >
          <Trans id="settings.organization.data-catalogs.brands.tab.overview.deposit-percentages.enabled">
            Enabled
          </Trans>
        </RadioItem>
        <RadioItem
          value="disabled"
          ariaLabel={t({
            id: 'settings.organization.data-catalogs.brands.tab.overview.deposit-percentages.disabled',
            message: 'Disabled',
          })}
        >
          <Trans id="settings.organization.data-catalogs.brands.tab.overview.deposit-percentages.disabled">
            Disabled
          </Trans>
        </RadioItem>
      </RadioItemGroup>

      {showDepositPercentagesInputs && (
        <>
          <Paragraph>
            <Trans id="settings.organization.data-catalogs.brands.tab.overview.deposit-percentages.description">
              Only partial percentage can be edited
            </Trans>
          </Paragraph>

          <InputNumber value={0} isDisabled style={{ alignSelf: 'start' }} />

          <Stack row gap="0.5rem" style={{ alignSelf: 'start' }}>
            <InputNumber
              minValue={0}
              maxValue={100}
              value={partialDepositPercentage}
              onChange={(newPartialDepositPercentage) => {
                setPartialDepositPercentage(newPartialDepositPercentage);
              }}
              isDisabled={!isEditing}
            />

            {isEditing ? (
              <SaveButton
                label={t({
                  id: 'settings.organization.data-catalogs.brands.tab.overview.deposit-percentages.save.label',
                  message: 'Save deposit percentages',
                })}
                onClick={handleSave}
                isLoading={isUpdateBrandPending}
              />
            ) : (
              <EditButton
                label={t({
                  id: 'settings.organization.data-catalogs.brands.tab.overview.deposit-percentages.edit.label',
                  message: 'Edit deposit percentages',
                })}
                onClick={() => setIsEditing(true)}
              />
            )}
          </Stack>

          <InputNumber value={100} isDisabled style={{ alignSelf: 'start' }} />
        </>
      )}

      {showErrors && <Message type="error">{error}</Message>}
    </Stack>
  );
};
